'use client';

import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast = ({ message, type, onClose }: ToastProps) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';

  return (
    <div
      className={`fixed right-4 top-4 z-[60] rounded-lg ${bgColor} px-4 py-2 text-white shadow-lg transition-all duration-300`}
    >
      <div className="flex items-center gap-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-200">
          ×
        </button>
      </div>
    </div>
  );
};

export const ClientIdSelector = () => {
  const [confirm, setConfirm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [currentClientId, setCurrentClientId] = useState('');
  const [environment, setEnvironment] = useState<'local' | 'production'>('local');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    // Get current client ID from cookies
    const existingClientId = Cookies.get('clientId');
    if (existingClientId) {
      setCurrentClientId(existingClientId);
      setClientId(existingClientId);
    }

    // Detect environment
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('dev')) {
      setEnvironment('local');
    } else {
      setEnvironment('production');
    }
  }, []);

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  };

  const handleConfirmClick = () => {
    if (clientId.trim() === '') {
      Cookies.remove('clientId');
      setCurrentClientId('');
      showToast('Client ID removed', 'info');

      // Clean redirect without params
      const url = new URL(window.location.href);
      url.search = '';
      window.location.href = url.toString();
    } else if (clientId.trim().length > 0) {
      // Validate client ID format (basic validation)
      if (clientId.trim().length < 3) {
        showToast('Client ID must be at least 3 characters long', 'error');
        return;
      }

      Cookies.set('clientId', clientId.trim(), {
        expires: environment === 'local' ? 1 : 30, // 1 day for local, 30 days for production
        secure: environment === 'production',
        sameSite: 'strict',
      });

      setCurrentClientId(clientId.trim());
      showToast(`Client ID set to ${clientId.trim()}`, 'success');

      // Smooth reload with a slight delay to show the toast
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    setConfirm(false);
  };

  const handleClear = () => {
    setClientId('');
    setConfirm(false);
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="w-full">
        <div className="relative flex w-full flex-col gap-3 rounded-xl bg-gray-50 p-4 dark:bg-gray-800/50">
          {/* Environment indicator */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Environment:</span>
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                environment === 'local'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
                  : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
              }`}
            >
              {environment === 'local' ? 'Development' : 'Production'}
            </span>
          </div>

          {/* Current Client ID display */}
          {currentClientId && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Current ID:</span>
              <span className="font-mono text-sm text-blue-600 dark:text-blue-400">{currentClientId}</span>
            </div>
          )}

          {/* Input section */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-blue-400"
                id="clientId"
                type="text"
                value={clientId}
                placeholder="Enter Client ID"
                onChange={(e) => setClientId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (confirm) {
                      handleConfirmClick();
                    } else {
                      setConfirm(true);
                    }
                  } else if (e.key === 'Escape') {
                    setConfirm(false);
                  }
                }}
              />
              {clientId && (
                <button
                  onClick={handleClear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                  type="button"
                  title="Clear"
                >
                  ×
                </button>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-1.5">
              {confirm ? (
                <>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500 text-white transition-colors hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                    onClick={handleConfirmClick}
                    title="Confirm changes"
                  >
                    ✓
                  </button>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-500 text-white transition-colors hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50"
                    onClick={() => setConfirm(false)}
                    title="Cancel"
                  >
                    ×
                  </button>
                </>
              ) : (
                <button
                  className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setConfirm(true)}
                  disabled={clientId === currentClientId}
                  title="Set Client ID"
                >
                  Set
                </button>
              )}
            </div>
          </div>

          {/* Help text */}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {confirm ? (
              <span className="text-amber-600 dark:text-amber-400">Press Enter or click ✓ to confirm, Esc or × to cancel</span>
            ) : (
              <span>
                {environment === 'local' ? 'ID expires in 1 day' : 'ID expires in 30 days'}
                {clientId === currentClientId && clientId && ' • No changes to apply'}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
